<template>
  <div class="tab-panel-container py-2 px-3">
    <DxTabPanel v-model:selected-index="selectedIndex" height="100%" tabs-position="left" icon-position="top">
      <DxItem v-if="tienePermisoVerFacturas && !accesoRestringido" :key="1" :title="intl.i18n.t('mis_datos.facturas.titulo')" class="tab-item"
        icon="fa-regular fa-file-invoice">
        <MisFacturas></MisFacturas>
      </DxItem>
      <DxItem v-if="tienePermisoVerComisiones && !accesoRestringido" :key="2" :title="intl.i18n.t('mis_datos.comisiones.titulo')" class="tab-item"
        icon="fa-regular fa-money-bill">
        <MisComisiones></MisComisiones>
      </DxItem>
      <DxItem v-if="tienePermisoEdicionDatosPersonales" :key="2" :title="intl.i18n.t('mis_datos.titulo')" class="tab-item" icon="fa-regular fa-user">
        <MisDatosPersonalesForm></MisDatosPersonalesForm>
      </DxItem>
      <DxItem v-if="tienePermisoEdicionEmpresaAgente" :key="3" :title="intl.i18n.t('mis_datos.mi_empresa.titulo')" class="tab-item"
        icon="fa-regular fa-briefcase">
        <MisDatosFiscalesForm></MisDatosFiscalesForm>
      </DxItem>
      <DxItem v-if="tienePermisoEdicionEmpresaInmobiliaria" :title="$t('mi_inmobiliaria.pestanas.datos')" icon="fa-solid fa-building">
        <MiEmpresaInmobiliariaForm :empresaInmobiliaria="empresaInmobiliaria"></MiEmpresaInmobiliariaForm>
      </DxItem>
      <DxItem v-if="tienePermisoEdicionDatosPersonales" :title="$t('mis_datos.pestanas.documentacion')" icon="fa-regular fa-folder-open">
        <MiDocumentacion></MiDocumentacion>
      </DxItem>
      <DxItem v-if="tienePermisoVerDelegacionesInmobiliaria && !accesoRestringido" :title="$t('mi_inmobiliaria.pestanas.delegaciones')"
        icon="fa-solid fa-location-dot">
        <MisDelegaciones :empresaInmobiliaria="empresaInmobiliaria"></MisDelegaciones>
      </DxItem>
    </DxTabPanel>
  </div>
</template>

<script setup lang="ts">
import { useIntl } from "@/.shared/services/i18n";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useAuthentication } from "@/.shared/services/auth";
import { registrarRecargaDatosEmpresaMisDatos } from "@/composables/misDatosRecargaEmpresa";
import MisFacturas from "@/components/mis-datos/MisFacturasGrid.vue";
import MisDatosPersonalesForm from "@/components/mis-datos/MisDatosPersonalesForm.vue";
import MisDatosFiscalesForm from "@/components/mis-datos/MiEmpresaColaboradoraForm.vue";
import MiEmpresaInmobiliariaForm from "@/components/mis-datos/MiEmpresaInmobiliariaForm.vue";
import MisDelegaciones from "@/components/mis-datos/MisDelegacionesInmobiliariasGrid.vue";
import MiDocumentacion from "@/components/mis-datos/MiDocumentacion.vue";
import MisComisiones from "@/components/mis-datos/MisComisiones.vue";
import DxTabPanel, { DxItem } from 'devextreme-vue/tab-panel';
import { useAuthorization } from "@/.shared/services/auth/authorization";
import { PermissionsApp } from "@/.shared/services/auth/authorization/authorization-service-impl";
import { useApi } from "@/.shared/.api/integration";
import { EditEmpresaInmobiliaria } from "@/components/mis-datos/models/EditEmpresaInmobiliaria";
import { useEmpresaAgentesPortalRestringido } from "@/composables/empresaAgentesPortalRestringido";

export type MisDatosTabId =
  | "facturas"
  | "comisiones"
  | "datos"
  | "empresa"
  | "inmobiliaria"
  | "documentacion"
  | "delegaciones";

const activeTab = defineModel<MisDatosTabId>("activeTab", { default: "facturas" });

const api = useApi();
const intl = useIntl();
const autho = useAuthorization();
const auth = useAuthentication();
const { accesoRestringido } = useEmpresaAgentesPortalRestringido();

let desregistrarRecargaEmpresa: (() => void) | undefined;

onMounted(() => {
  if (autho.hasPermission(PermissionsApp.EditSelfEmpresaInmobiliaria)) {
    cargarEmpresaInmobiliaria();
  }

  desregistrarRecargaEmpresa = registrarRecargaDatosEmpresaMisDatos(async () => {
    await auth.refreshUserInfo();
    if (autho.hasPermission(PermissionsApp.EditSelfEmpresaInmobiliaria)) {
      await cargarEmpresaInmobiliaria();
    }
  });
});

onUnmounted(() => {
  desregistrarRecargaEmpresa?.();
});

const empresaInmobiliaria = ref<EditEmpresaInmobiliaria>(new EditEmpresaInmobiliaria());
const tienePermisoEdicionEmpresaAgente = autho.hasPermission(PermissionsApp.EditEmpresaAgente);
const tienePermisoEdicionDatosPersonales = autho.hasPermission(PermissionsApp.EditDatosPersonalesAgente);
const tienePermisoVerFacturas = autho.hasPermission(PermissionsApp.ViewSameCompanyFacturas);
const tienePermisoVerComisiones = autho.hasPermission(PermissionsApp.SearchRetribucionesEnergia);
const tienePermisoEdicionEmpresaInmobiliaria = autho.hasPermission(PermissionsApp.EditSelfEmpresaInmobiliaria);
const tienePermisoVerDelegacionesInmobiliaria = autho.hasPermission(PermissionsApp.EditSameCompanyDelegacion);

const selectedIndex = ref(0);

const visibleTabs = computed((): MisDatosTabId[] => {
  const tabs: MisDatosTabId[] = [];
  if (tienePermisoVerFacturas && !accesoRestringido.value) {
    tabs.push("facturas");
  }
  if (tienePermisoVerComisiones && !accesoRestringido.value) {
    tabs.push("comisiones");
  }
  if (tienePermisoEdicionDatosPersonales) {
    tabs.push("datos");
  }
  if (tienePermisoEdicionEmpresaAgente) {
    tabs.push("empresa");
  }
  if (tienePermisoEdicionEmpresaInmobiliaria) {
    tabs.push("inmobiliaria");
  }
  if (tienePermisoEdicionDatosPersonales) {
    tabs.push("documentacion");
  }
  if (tienePermisoVerDelegacionesInmobiliaria && !accesoRestringido.value) {
    tabs.push("delegaciones");
  }
  return tabs;
});

function syncSelectedIndexFromActiveTab(): void {
  const index = visibleTabs.value.indexOf(activeTab.value!);
  selectedIndex.value = index >= 0 ? index : 0;
}

watch(visibleTabs, syncSelectedIndexFromActiveTab, { immediate: true });

watch(activeTab, () => {
  syncSelectedIndexFromActiveTab();
});

watch(selectedIndex, (index) => {
  const tab = visibleTabs.value[index];
  if (tab != null && tab !== activeTab.value) {
    activeTab.value = tab;
  }
});

const cargarEmpresaInmobiliaria = async (): Promise<void> => {
  let empresaInmobiliariaResult = await api.callStandardApi('getEmpresaInmobiliariaUsuarioRequest', {});

  let editEmpresaInmobiliaria = new EditEmpresaInmobiliaria();
  editEmpresaInmobiliaria.createFromDetailEmpresaInmobiliaria(empresaInmobiliariaResult);
  empresaInmobiliaria.value = editEmpresaInmobiliaria;
}

</script>
<style lang="scss" scoped>
.mis-comisiones .tab-panel-container{
    overflow: hidden;
    height: 100%;
}
</style>